import springboard from 'springboard';

import {StateSupervisor} from 'springboard/services/states/shared_state_service';
import {DBObjects} from '@/store/store_types';
import {SpringboardRegistry} from 'springboard/engine/register';
import {notifications} from '@mantine/notifications';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        User: UserModuleReturnValue;
    }
}

export type UserProfile = {
    username?: string;
    profile_picture_file_id?: string | null;
}

type UserModuleReturnValue = {
    currentUser: StateSupervisor<(DBObjects['User'] & {emailShort?: string}) | null>;
    refreshUser: (token?: string) => Promise<DBObjects['User'] | null>;
    updateProfile: (args: UserProfile) => Promise<void>;
    isOfflineMode: StateSupervisor<boolean>;
    continueAsOffline: (navigate: (pathname: string) => void) => void;
    ensureLocalUser: (args: {name: string; ensureWorkspace?: boolean}) => Promise<DBObjects['User'] | null>;
    Provider: (props: React.PropsWithChildren) => React.ReactNode;
};

import {getApiHost} from '@/store/utils/get_api_host';
import {isReactNativeWeb} from '@/utils/user_agent';
import {generateId} from '@/store/utils/generate_id';
import {navigateToPathForReason, setHashForReason, setPathnameForReason} from '../navigation/navigation_module';
import {useEffect} from 'react';
import {Selectors} from '@/store/selectors/selectors';
const API_HOST = getApiHost();

export default (sb: SpringboardRegistry) => sb.registerModule('User', {rpcMode: 'local'}, async (moduleAPI): Promise<UserModuleReturnValue> => {
    const currentUser = await moduleAPI.statesAPI.createUserAgentState<(DBObjects['User'] & {emailShort?: string}) | null>('currentUser', null);
    const isOfflineMode = await moduleAPI.statesAPI.createUserAgentState<boolean>('isOfflineMode', false);

    const continueAsOffline = async (navigate: (pathname: string) => void) => {
        isOfflineMode.setState(true);

        const localMe = await ensureLocalUser({name: 'User'});
        if (localMe) {
            await ensureLocalWorkspace({username: localMe.name || 'User'});

            currentUser.setState(localMe);
            navigateToPathForReason(navigate, 'navigate_to_dashboard_from_continue_as_offline', '/dashboard');
        }
    };

    const updateProfile = async (profile: UserProfile) => {
        let token = '';

        if (!token) {
            const localStorageToken = localStorage.getItem('RN_STORED_TOKEN');
            if (localStorageToken) {
                token = localStorageToken;
            }
        }

        try {
            const payload: {username?: string; profile_picture_file_id?: string | null} = {};
            if (profile.username !== undefined) {
                payload.username = profile.username;
            }
            if (profile.profile_picture_file_id !== undefined) {
                payload.profile_picture_file_id = profile.profile_picture_file_id;
            }

            const res = await fetch(`${API_HOST}/users/me`, {
                method: 'PUT',
                body: JSON.stringify(payload),
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                const errorText = await res.text();
                notifications.show({
                    title: 'Profile update failed',
                    message: errorText || 'Failed to update profile. Please try again.',
                    color: 'red',
                });
                return;
            }

            refreshUser();
        } catch (e) {
            notifications.show({
                title: 'Error updating profile',
                message: `An unexpected error occurred: ${e}`,
                color: 'red',
            });
        }

    };

    const refreshUser = async (token?: string) => {
        if (isOfflineMode.getState()) {
            return currentUser.getState();
        }

        const localStorageToken = localStorage.getItem('RN_STORED_TOKEN');

        if (!token) {
            if (localStorageToken) {
                token = localStorageToken;
            }
        }

        try {
            const user: (DBObjects['User'] & {emailShort: string}) | {error: string} = await fetch(`${API_HOST}/users/me`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                },
            }).then(r => r.json());
            if ('error' in user) {
                currentUser.setState(null);
                if (isReactNativeWeb()) {
                    if (location.hash && location.hash !== '/main') {
                        setHashForReason('redirect_to_main_due_to_fetch_me_failed_on_init', '/main');
                    }
                } else {
                    if (location.pathname && location.pathname !== '/main') {
                        setPathnameForReason('redirect_to_main_due_to_fetch_me_failed_on_init', '/main');
                    }
                }
                return null;
            }

            currentUser.setState(user);
            return user;
        } catch (e) {
            return null;
        }
    };

    let ensured: Awaited<ReturnType<Selectors['getMe']>> | undefined;
    let ensuring = false;

    const ensureLocalUser = moduleAPI.createAction('ensureLocalUser', {}, async (args: {name: string; ensureWorkspace?: boolean}): Promise<DBObjects['User'] | null> => {
        if (ensured) {
            return ensured;
        }

        if (ensuring) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    if (ensured) {
                        resolve(ensured);
                    } else {
                        reject('failed to handle local auth race condition')
                    }
                }, 1000);
            });
        }

        ensuring = true;

        const kyselyModule = moduleAPI.getModule('Kysely');
        try {
            const localMe = await kyselyModule.selectors.getMe({}, {mode: 'local'});
            if (localMe) {
                ensured = localMe;
                return ensured;
            }
        } catch (e) {
            console.error(e);
        }

        const id = generateId();

        try {
            const db = kyselyModule.db();
            const user = await db.insertInto('User').values({
                id,
                name: args.name,
            }).execute()
                .then(() => db.selectFrom('User').where('id', '=', id).selectAll().executeTakeFirstOrThrow());
            ensured = user;

            await db.insertInto('Workspace').values({
                id: id,
                name: `${args.name}'s User Workspace`,
                owner_id: id,
                created_by: id,
                type: 'USER',
            }).execute();

            if (args.ensureWorkspace) {
                await ensureLocalWorkspace({username: args.name});
            }

            return ensured;
        } catch (e) {
            console.error(e);
        }

        return null;
    });

    const ensureLocalWorkspace = moduleAPI.createAction('ensureLocalWorkspace', {}, async (args: {username: string}): Promise<DBObjects['Workspace'] | null> => {
        const kyselyModule = moduleAPI.getModule('Kysely');
        try {
            const localWorkspaces = await kyselyModule.selectors.getAllWorkspaces({}, {mode: 'local'});
            if (localWorkspaces.length > 0) {
                return localWorkspaces[0];
            }
        } catch (e) {
            console.error(e);
        }

        try {
            const workspace = await kyselyModule.actions.createWorkspace({
                name: `${args.username}'s Workspace`,
                type: 'LOCAL',
            }, {mode: 'local'});

            return workspace;
        } catch (e) {
            console.error(e);
        }

        return null;
    });

    
    await refreshUser();
    

    return {
        currentUser,
        refreshUser,
        updateProfile,
        isOfflineMode,
        continueAsOffline,
        ensureLocalUser,
        Provider: (props: React.PropsWithChildren) => {
            const currentUserValue = currentUser.useState();

            useEffect(() => {
                if (currentUserValue) {
                    ensureLocalUser({name: currentUserValue.name});
                }
            }, [currentUser]);

            return props.children;
        },
    };
});
